// v7.56 — Creative Layers store (zustand, React-free). Mirrors the shape of
// creative-edit-store.ts: per-tile prepare entries (splits run concurrently
// and keep going in the background), one ACTIVE editor, debounced autosave,
// and saveToCanvas = full-res Konva export → Bunny → copy beside the original
// (or in-place update for an already-exported copy).
import { create } from 'zustand';
import type Konva from 'konva';

export interface LayerObject {
  id: string;
  url: string;
  name: string;
  x: number; y: number; w: number; h: number;
  z: number;
  rotation?: number;
  srcW: number; srcH: number;
}

export interface LayersDoc {
  baseUrl: string;
  width: number;
  height: number;
  layers: LayerObject[];
}

export interface LayersPrepareState {
  runId: number;
  sourceUrl: string;
  tileW?: number;
  tileH?: number;
  docId: number | null;
  phase: 'preparing' | 'ready' | 'failed';
  phaseNote: string;
  error: string | null;
  doc: LayersDoc | null;
}

interface CreativeLayersState {
  prepares: Record<string, LayersPrepareState>;
  activeImageId: string | null;
  /** Primary selection (Transformer target). */
  selectedLayerId: string | null;
  /** v7.57 — full multi-selection (shift-click); always contains selectedLayerId when set. */
  selectedLayerIds: string[];
  saving: boolean;
  /** v7.57 — server-side grouping in flight. */
  grouping: 'merge' | 'bake' | null;
  /** Bumped when a layer bitmap finishes loading so the Transformer can rebind. */
  imagesVersion: number;
  stage: Konva.Stage | null;

  open: (imageId: string, sourceUrl: string, tileW?: number, tileH?: number) => void;
  close: () => void;
  dismiss: (imageId: string) => void;
  setStage: (stage: Konva.Stage | null) => void;
  setSelected: (id: string | null) => void;
  /** Shift-click: add/remove one layer from the selection. */
  toggleSelected: (id: string) => void;
  bumpImages: () => void;
  updateLayer: (id: string, patch: Partial<LayerObject>) => void;
  /** Move one step up (towards the viewer) or down in the stack. */
  reorderLayer: (id: string, dir: 'up' | 'down') => void;
  deleteLayer: (id: string) => void;
  /** v7.57 — merge the selected layers into one (server composites). */
  mergeSelected: () => Promise<string | null>;
  /** v7.57 — bake the selected layers into the background (server composites). */
  bakeSelected: () => Promise<string | null>;
  saveToCanvas: () => Promise<'copy' | 'updated' | string>;
}

export const LAYERS_EXPORT_MARKER = '/creative-layers/exports/';

let nextRunId = 1;
let autosaveTimer: ReturnType<typeof setTimeout> | null = null;

function patchDoc(docId: number, doc: LayersDoc): void {
  void fetch(`/api/creative-layers/docs/${docId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ doc }),
  }).catch(() => { /* autosave is best-effort */ });
}

function scheduleAutosave(): void {
  if (autosaveTimer) clearTimeout(autosaveTimer);
  autosaveTimer = setTimeout(() => { autosaveTimer = null; flushAutosave(); }, 1200);
}

function flushAutosave(): void {
  if (autosaveTimer) { clearTimeout(autosaveTimer); autosaveTimer = null; }
  const s = useCreativeLayersStore.getState();
  const p = s.activeImageId ? s.prepares[s.activeImageId] : null;
  if (p?.docId && p.doc) patchDoc(p.docId, p.doc);
}

function setPrepare(imageId: string, patch: Partial<LayersPrepareState>): void {
  useCreativeLayersStore.setState((s) => {
    const cur = s.prepares[imageId];
    if (!cur) return s;
    return { prepares: { ...s.prepares, [imageId]: { ...cur, ...patch } } };
  });
}

function owns(imageId: string, runId: number): boolean {
  return useCreativeLayersStore.getState().prepares[imageId]?.runId === runId;
}

/** Update the ACTIVE doc's layers immutably; no-op when nothing is active. */
function mutateActiveLayers(fn: (layers: LayerObject[]) => LayerObject[]): void {
  useCreativeLayersStore.setState((st) => {
    const aid = st.activeImageId;
    const p = aid ? st.prepares[aid] : null;
    if (!aid || !p?.doc) return st;
    return { prepares: { ...st.prepares, [aid]: { ...p, doc: { ...p.doc, layers: fn(p.doc.layers) } } } };
  });
  scheduleAutosave();
}

function startPrepare(imageId: string, sourceUrl: string, tileW?: number, tileH?: number): void {
  const runId = nextRunId++;
  useCreativeLayersStore.setState((s) => ({
    prepares: {
      ...s.prepares,
      [imageId]: {
        runId, sourceUrl, tileW, tileH, docId: null, phase: 'preparing',
        phaseNote: 'Splitting the ad into layers… this takes a few minutes.', error: null, doc: null,
      },
    },
  }));
  void (async () => {
    try {
      const res = await fetch('/api/creative-layers/prepare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl: sourceUrl }),
      });
      if (res.status === 402) throw new Error('Not enough credits to split this creative.');
      if (!res.ok && res.status !== 202) throw new Error((await res.text()).slice(0, 200));
      const { docId } = await res.json();
      if (!owns(imageId, runId)) return;
      setPrepare(imageId, { docId });
      // Poll every 3s for up to 15 min (the split alone is ~5 min).
      for (let i = 0; i < 300; i++) {
        const dRes = await fetch(`/api/creative-layers/docs/${docId}`);
        if (!owns(imageId, runId)) return;
        if (dRes.ok) {
          const d = await dRes.json();
          if (d.status === 'ready' && d.doc) {
            setPrepare(imageId, { doc: d.doc as LayersDoc, phase: 'ready' });
            return;
          }
          if (d.status === 'failed') throw new Error(d.error || 'Splitting the creative failed.');
        }
        if (i === 20) setPrepare(imageId, { phaseNote: 'Still splitting — every element is rendered on its own…' });
        if (i === 80) setPrepare(imageId, { phaseNote: 'Almost there — placing the layers…' });
        await new Promise((r) => setTimeout(r, 3000));
      }
      throw new Error('Timed out splitting the creative.');
    } catch (e) {
      if (!owns(imageId, runId)) return;
      setPrepare(imageId, { error: e instanceof Error ? e.message : 'Splitting the creative failed.', phase: 'failed' });
    }
  })();
}

/** POST the selection to /merge or /bake and swap in the returned doc. Returns an error string or null. */
async function groupSelected(kind: 'merge' | 'bake'): Promise<string | null> {
  const st = useCreativeLayersStore.getState();
  const aid = st.activeImageId;
  const p = aid ? st.prepares[aid] : null;
  if (!aid || !p?.doc || !p.docId) return 'Editor not ready.';
  const ids = st.selectedLayerIds;
  if (kind === 'merge' && ids.length < 2) return 'Select at least two pieces to merge.';
  if (ids.length < 1) return 'Select a piece first.';
  if (st.grouping) return null;
  flushAutosave();
  useCreativeLayersStore.setState({ grouping: kind });
  try {
    const res = await fetch(`/api/creative-layers/docs/${p.docId}/${kind}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ layerIds: ids }),
    });
    if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error ?? `${kind} failed.`);
    const { doc } = (await res.json()) as { doc: LayersDoc };
    useCreativeLayersStore.setState((s) => {
      const cur = s.prepares[aid];
      return cur ? { prepares: { ...s.prepares, [aid]: { ...cur, doc } }, selectedLayerId: null, selectedLayerIds: [] } : s;
    });
    return null;
  } catch (e) {
    return e instanceof Error ? e.message : `${kind} failed.`;
  } finally {
    useCreativeLayersStore.setState({ grouping: null });
  }
}

export const useCreativeLayersStore = create<CreativeLayersState>((set, get) => ({
  prepares: {},
  activeImageId: null,
  selectedLayerId: null,
  selectedLayerIds: [],
  saving: false,
  grouping: null,
  imagesVersion: 0,
  stage: null,

  open: (imageId, sourceUrl, tileW, tileH) => {
    const s = get();
    if (s.activeImageId && s.activeImageId !== imageId) flushAutosave();
    set({ activeImageId: imageId, selectedLayerId: null, selectedLayerIds: [], saving: false, grouping: null });
    const existing = s.prepares[imageId];
    if (existing && existing.phase !== 'failed') {
      if (tileW && tileH) setPrepare(imageId, { tileW, tileH });
      return; // ready → instant open; preparing → watch it
    }
    startPrepare(imageId, sourceUrl, tileW, tileH);
  },

  close: () => {
    flushAutosave();
    set({ activeImageId: null, selectedLayerId: null, selectedLayerIds: [], saving: false, grouping: null, stage: null });
  },

  dismiss: (imageId) => {
    set((st) => {
      const prepares = { ...st.prepares };
      delete prepares[imageId];
      return { prepares, ...(st.activeImageId === imageId ? { activeImageId: null } : {}) };
    });
  },

  setStage: (stage) => set({ stage }),
  setSelected: (selectedLayerId) => set({ selectedLayerId, selectedLayerIds: selectedLayerId ? [selectedLayerId] : [] }),
  toggleSelected: (id) => set((s) => {
    const has = s.selectedLayerIds.includes(id);
    const selectedLayerIds = has ? s.selectedLayerIds.filter((x) => x !== id) : [...s.selectedLayerIds, id];
    const selectedLayerId = has ? (selectedLayerIds[selectedLayerIds.length - 1] ?? null) : id;
    return { selectedLayerIds, selectedLayerId };
  }),
  bumpImages: () => set((s) => ({ imagesVersion: s.imagesVersion + 1 })),

  updateLayer: (id, patch) => {
    mutateActiveLayers((layers) => layers.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  },

  reorderLayer: (id, dir) => {
    mutateActiveLayers((layers) => {
      const sorted = [...layers].sort((a, b) => a.z - b.z);
      const i = sorted.findIndex((l) => l.id === id);
      const j = dir === 'up' ? i + 1 : i - 1;
      if (i < 0 || j < 0 || j >= sorted.length) return layers;
      [sorted[i], sorted[j]] = [sorted[j], sorted[i]];
      return sorted.map((l, idx) => ({ ...l, z: idx + 1 }));
    });
  },

  deleteLayer: (id) => {
    mutateActiveLayers((layers) => layers.filter((l) => l.id !== id));
    set((s) => ({ selectedLayerId: s.selectedLayerId === id ? null : s.selectedLayerId, selectedLayerIds: s.selectedLayerIds.filter((x) => x !== id) }));
  },

  mergeSelected: async () => groupSelected('merge'),
  bakeSelected: async () => groupSelected('bake'),

  saveToCanvas: async () => {
    const st = get();
    const aid = st.activeImageId;
    const p = aid ? st.prepares[aid] : null;
    const { stage, saving } = st;
    if (!aid || !p?.doc || !stage || saving) return 'Editor not ready.';
    const doc = p.doc;
    set({ saving: true, selectedLayerId: null, selectedLayerIds: [] });
    try {
      // setTimeout, not requestAnimationFrame — rAF never fires in a hidden tab.
      await new Promise((r) => setTimeout(r, 80));
      try {
        (stage.find('Transformer') as unknown as Array<{ nodes: (n: unknown[]) => void }>).forEach((tr) => tr.nodes([]));
      } catch { /* non-fatal */ }
      // Never export before every bitmap (base + layers) has loaded.
      for (let i = 0; i < 40; i++) {
        const imgs = stage.find('Image') as unknown as Konva.Image[];
        const allLoaded = imgs.length >= 1 && imgs.every((n) => {
          const el = n.image() as HTMLImageElement | undefined;
          return !!el && el.complete && el.naturalWidth > 0;
        });
        if (allLoaded) break;
        if (i === 39) { set({ saving: false }); return 'Layers still loading — try Save again in a moment.'; }
        await new Promise((r) => setTimeout(r, 200));
      }
      stage.draw();
      const scale = stage.scaleX() || 1;
      const dataUrl = stage.toDataURL({
        x: 0, y: 0,
        width: doc.width * scale, height: doc.height * scale,
        pixelRatio: 1 / scale, mimeType: 'image/png',
      });
      const res = await fetch('/api/creative-layers/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dataUrl, doc }),
      });
      if (!res.ok) throw new Error((await res.text()).slice(0, 200));
      const { url, docId: newDocId } = await res.json();
      const cs = (await import('./canvas-store')).useCanvasStore.getState();
      const isEditedCopy = (p.sourceUrl ?? '').includes(LAYERS_EXPORT_MARKER);
      if (isEditedCopy) {
        cs.updateImage(aid, { src: url });
        set((s2) => {
          const cur = s2.prepares[aid];
          return cur ? {
            saving: false,
            prepares: { ...s2.prepares, [aid]: { ...cur, sourceUrl: url, docId: typeof newDocId === 'number' ? newDocId : cur.docId } },
          } : { saving: false };
        });
        return 'updated';
      }
      cs.addImageNextTo(
        url,
        p.tileW ?? doc.width,
        p.tileH ?? doc.height,
        `layers-${Date.now()}.png`,
        aid,
        doc.width,
        doc.height,
      );
      set({ saving: false });
      return 'copy';
    } catch (e) {
      set({ saving: false });
      return e instanceof Error ? e.message : 'Export failed';
    }
  },
}));
